import type { MenuItem } from '@/components/maia/types'
import { inferAllergens } from './allergens'

export type WeeklyDishType = 'primo' | 'secondo'

export interface WeeklyDish {
  id: string
  type: WeeklyDishType
  title: string
  description: string
  image?: string
  price?: string
  allergens?: string
  createdAt: number
  updatedAt?: number
}

export const WEEKLY_DISHES_KEY = 'maia-weekly-dishes'
export const WEEKLY_DISHES_SEEDED_KEY = 'maia-weekly-dishes-seeded'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function getWeeklyDishes(): WeeklyDish[] {
  try {
    const raw = localStorage.getItem(WEEKLY_DISHES_KEY)
    return raw ? (JSON.parse(raw) as WeeklyDish[]) : []
  } catch {
    return []
  }
}

function writeWeeklyDishes(dishes: WeeklyDish[]): void {
  localStorage.setItem(WEEKLY_DISHES_KEY, JSON.stringify(dishes))
}

export function saveWeeklyDish(
  data: Omit<WeeklyDish, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
): WeeklyDish {
  const dishes = getWeeklyDishes()
  const now = Date.now()

  if (data.id) {
    const index = dishes.findIndex((d) => d.id === data.id)
    if (index === -1) {
      // Fallback to create if id not found.
      const created: WeeklyDish = {
        ...data,
        id: data.id,
        createdAt: now,
        updatedAt: now,
      }
      writeWeeklyDishes([created, ...dishes])
      return created
    }

    const updated: WeeklyDish = {
      ...dishes[index],
      ...data,
      updatedAt: now,
    }
    dishes[index] = updated
    writeWeeklyDishes(dishes)
    return updated
  }

  const created: WeeklyDish = {
    ...data,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
  }
  writeWeeklyDishes([created, ...dishes])
  return created
}

export function deleteWeeklyDish(id: string): boolean {
  const dishes = getWeeklyDishes()
  const next = dishes.filter((d) => d.id !== id)
  if (next.length === dishes.length) return false
  writeWeeklyDishes(next)
  return true
}

export function clearWeeklyDishes(): void {
  localStorage.removeItem(WEEKLY_DISHES_KEY)
  localStorage.removeItem(WEEKLY_DISHES_SEEDED_KEY)
}

export function setWeeklyDishes(dishes: WeeklyDish[]): void {
  const now = Date.now()
  writeWeeklyDishes(
    dishes.map((d) => ({
      ...d,
      updatedAt: now,
    }))
  )
}

export function seedWeeklyDishesFromFallback(fallback: {
  primis: MenuItem[]
  seconds: MenuItem[]
}): void {
  try {
    const alreadySeeded = localStorage.getItem(WEEKLY_DISHES_SEEDED_KEY) === '1'
    const dishes = getWeeklyDishes()
    const allFallback = [...fallback.primis, ...fallback.seconds]

    const fallbackPriceByTitle = new Map<string, string>(
      allFallback
        .filter((item): item is MenuItem & { price: string } => !!item.price)
        .map((item) => [item.title, item.price])
    )

    const fallbackAllergensByTitle = new Map<string, string>(
      allFallback
        .filter((item): item is MenuItem & { allergens: string } => !!item.allergens)
        .map((item) => [item.title, item.allergens])
    )

    if (dishes.length > 0) {
      const needsBackfill = dishes.some(
        (dish) =>
          (!dish.price && fallbackPriceByTitle.has(dish.title)) ||
          (!dish.allergens &&
            (fallbackAllergensByTitle.has(dish.title) ||
              inferAllergens(dish.title, 'it').length > 0))
      )
      if (needsBackfill) {
        const updated = dishes.map((dish) => ({
          ...dish,
          price: dish.price || fallbackPriceByTitle.get(dish.title) || dish.price,
          allergens:
            dish.allergens ||
            fallbackAllergensByTitle.get(dish.title) ||
            inferAllergens(dish.title, 'it') ||
            dish.allergens,
        }))
        writeWeeklyDishes(updated)
      }
      localStorage.setItem(WEEKLY_DISHES_SEEDED_KEY, '1')
      return
    }

    if (alreadySeeded) return

    const now = Date.now()
    const seeded: WeeklyDish[] = [
      ...fallback.primis.map(
        (item): WeeklyDish => ({
          id: generateId(),
          type: 'primo',
          title: item.title,
          description: item.description ?? '',
          price: item.price,
          allergens: item.allergens || inferAllergens(item.title, 'it'),
          image: item.image,
          createdAt: now,
        })
      ),
      ...fallback.seconds.map(
        (item): WeeklyDish => ({
          id: generateId(),
          type: 'secondo',
          title: item.title,
          description: item.description ?? '',
          price: item.price,
          allergens: item.allergens || inferAllergens(item.title, 'it'),
          image: item.image,
          createdAt: now,
        })
      ),
    ]

    writeWeeklyDishes(seeded)
    localStorage.setItem(WEEKLY_DISHES_SEEDED_KEY, '1')
  } catch {
    // ignore seeding errors
  }
}

export function groupWeeklyDishes(dishes: WeeklyDish[]): {
  primis: WeeklyDish[]
  seconds: WeeklyDish[]
} {
  return {
    primis: dishes.filter((d) => d.type === 'primo'),
    seconds: dishes.filter((d) => d.type === 'secondo'),
  }
}

export function getStoredMenuOrFallback(fallback: {
  primis: MenuItem[]
  seconds: MenuItem[]
}): {
  primis: MenuItem[]
  seconds: MenuItem[]
} {
  const dishes = getWeeklyDishes()
  if (dishes.length === 0) return fallback

  const grouped = groupWeeklyDishes(dishes)

  const mergeFallback = (
    stored: WeeklyDish[],
    fallbackItems: MenuItem[]
  ): MenuItem[] => {
    const fallbackPriceByTitle = new Map<string, string>(
      fallbackItems
        .filter((item): item is MenuItem & { price: string } => !!item.price)
        .map((item) => [item.title, item.price])
    )

    const fallbackAllergensByTitle = new Map<string, string>(
      fallbackItems
        .filter((item): item is MenuItem & { allergens: string } => !!item.allergens)
        .map((item) => [item.title, item.allergens])
    )

    return stored.map((d) => ({
      id: d.id,
      title: d.title,
      description: d.description,
      price: d.price || fallbackPriceByTitle.get(d.title) || d.price,
      allergens:
        d.allergens ||
        fallbackAllergensByTitle.get(d.title) ||
        inferAllergens(d.title, 'it') ||
        d.allergens,
      image: d.image,
    }))
  }

  return {
    primis: mergeFallback(grouped.primis, fallback.primis),
    seconds: mergeFallback(grouped.seconds, fallback.seconds),
  }
}
